import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import sorting from '../../Utils/sorting';
import helpers from '../../Utils/helpers';

function InvoiceListVoss({ invoices, selectInvoice }) {
    const { t } = useTranslation();
    
    const sortedInvoices = useMemo(() => {
        return [...invoices].sort(sorting.compareValues('invoiceDate', 'desc'));
    }, [invoices]);

    const [activeInvoiceId, setActiveInvoiceId] = useState(null);

    return (
        <div>
            <table className="table table-stripe table-active table-invoices">
                <thead>
                    <tr>
                        <th className="text-left">{t('invoicelist.invoicenumber')}</th>
                        <th className="text-left">{t('invoicelist.invoicedate')}</th>
                        <th className="text-left">{t('invoicelist.expirydate')}</th>
                        <th className="text-left">{t('invoicelist.amount')}</th>
                        <th className="text-left">{t('invoicelist.residualamount')}</th>
                        <th className="text-left">{t('invoicelist.paidunpaid')}</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedInvoices.map((invoice) => (
                        <tr
                            key={invoice.id}
                            onClick={() => {
                                setActiveInvoiceId(invoice.id);
                                selectInvoice(invoice.id);
                            }}
                            className={invoice.id === activeInvoiceId ? 'table-active-row' : ''}
                        >
                            <td>{invoice.invoiceNumber}</td>
                            <td>{helpers.formatDate(invoice.invoiceDate)}</td>
                            <td>{invoice.paymentDueDate ? helpers.formatDate(invoice.paymentDueDate) : ''}</td>
                            <td className="text-right">{invoice.totalAmount}</td>
                            <td className="text-right">{invoice.unpaidAmount}</td>
                            <td>{t(`invoicelist.${invoice.paymentStatus === 0 ? 'Unpaid' : 'Paid'}`)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function InvoiceDetailsVoss({ invoice, primaryCustomerOrgNo }) {
    const { t } = useTranslation();
    const [activeOrgNumber, setActiveOrgNumber] = useState(null);

    if (!invoice || !invoice.subscriptions || !invoice.subscriptions.length) {
        return <div className="text-center"><p className='m-0'>{t('invoicelist.noSubscriptionsFound')}</p></div>;
    }

    const normalizeOrgNumber = (orgNumber) => {
        return orgNumber ? orgNumber.replace(/-/g, '') : '';
    };

    const renderPeriod = (period) => {
        if (!period || !period.startDate || !period.endDate) return '';
        return `${helpers.formatDate(period.startDate)} - ${helpers.formatDate(period.endDate)}`;
    };

    const normalizedPrimaryCustomerOrgNumber = normalizeOrgNumber(primaryCustomerOrgNo);

    const primaryCustomerSubscriptions = invoice.subscriptions.filter((s) => normalizeOrgNumber(s.customer.organizationNumber) === normalizedPrimaryCustomerOrgNumber);

    const connectedOrgNumbers = [
        ...new Set(invoice.subscriptions.map((sub) => normalizeOrgNumber(sub.customer.organizationNumber)).filter((orgNo) => orgNo !== normalizedPrimaryCustomerOrgNumber)),
    ];

    const getSubscriptionsForOrg = (normalizedOrgNo) => {
        return invoice.subscriptions.filter((s) => normalizeOrgNumber(s.customer.organizationNumber) === normalizedOrgNo);
    };

    const calculateTotals = (subscriptions) => {
        return subscriptions.reduce(
            (totals, subscription) => {
                subscription.items.forEach((item) => {
                    totals.amount += item.amount;
                    totals.totalAmount += item.totalAmount;
                });
                return totals;
            },
            { amount: 0, totalAmount: 0 }
        );
    };

    const handleRowClick = (orgNumber) => {
        setActiveOrgNumber((prevOrgNumber) => (prevOrgNumber === orgNumber ? null : orgNumber));
    };

    const renderSubscriptions = (subscription, groupKey) => {
        let subscriptionName = subscription.name;
        return subscription.items.map((row, index) => {
            const uniqueKey = `${groupKey}-${subscription.id || subscription.name}-${row.name}-${index}`;

            const result = (
                <tr key={uniqueKey}>
                    <td>{subscriptionName}</td>
                    <td>{row.name}</td>
                    <td>{renderPeriod(row.period)}</td>
                    <td className="text-right">{row.quantity}</td>
                    <td className="text-right">{row.unitPrice}</td>
                    <td className="text-right">{row.amount}</td>
                    <td className="text-right">{row.totalAmount}</td>
                </tr>
            );

            subscriptionName = '';
            return result;
        });
    };

    const renderPrimaryCustomerSection = () => {
        return primaryCustomerSubscriptions.map((subscription) => renderSubscriptions(subscription, normalizedPrimaryCustomerOrgNumber));
    };

    const renderSeparatorRow = () => {
        if (connectedOrgNumbers.length === 0 || primaryCustomerSubscriptions.length === 0) return null;

        return (
            <tr className="non-interactive-row">
                <td colSpan={7} className="p-0" style={{ height: '40px' }}>
                    <hr className="m-0" style={{ borderColor: '#DADADA' }}></hr>
                </td>
            </tr>
        );
    };

    const renderConnectedOrganizations = () => {
        return connectedOrgNumbers.map((normalizedOrgNumber) => {
            const currentSubscriptions = getSubscriptionsForOrg(normalizedOrgNumber);
            const customer = currentSubscriptions[0].customer;
            const totals = calculateTotals(currentSubscriptions);
            const isExpanded = activeOrgNumber === normalizedOrgNumber;

            return (
                <React.Fragment key={normalizedOrgNumber}>
                    <tr onClick={() => handleRowClick(normalizedOrgNumber)}>
                        <td colSpan={5} className="bold">
                            <span className={`caret caret-lg ${isExpanded ? 'open' : ''}`}></span>
                            <span className="ml-24 d-inline-block">{customer.organizationNumber}</span>
                            <span className="ml-24 d-inline-block">{customer.name}</span>
                        </td>
                        <td className="bold text-right">{totals.amount}</td>
                        <td className="bold text-right">{totals.totalAmount}</td>
                    </tr>

                    {isExpanded && currentSubscriptions.map((subscription) => renderSubscriptions(subscription, normalizedOrgNumber))}
                </React.Fragment>
            );
        });
    };

    return (
        <table className="table table-stripe table-active table-invoices">
            <thead>
                <tr className='text-left'>
                    <th>{t('invoicelist.subscription')}</th>
                    <th>{t('invoicelist.service')}</th>
                    <th>{t('invoicelist.period')}</th>
                    <th>{t('invoicelist.quantity')}</th>
                    <th>{t('invoicelist.unitprice')}</th>
                    <th>{t('invoicelist.amount')}</th>
                    <th>{t('invoicelist.totalamount')}</th>
                </tr>
            </thead>
            <tbody>
                {renderPrimaryCustomerSection()}
                {renderSeparatorRow()}
                {renderConnectedOrganizations()}
            </tbody>
        </table>
    );
}

export default InvoiceListVoss;
